import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

export type MAXAuthStatus = "idle" | "verifying" | "success" | "error";

interface MAXAuthResult {
  status: MAXAuthStatus;
  error: string | null;
  retry: () => void;
}

/**
 * Extracts MAX initData from window URL.
 * Supports:
 *   - hash: #tgWebAppData=... or #initData=... or raw query-like hash
 *   - search: ?initData=... or ?tgWebAppData=...
 */
function extractInitData(): string | null {
  if (typeof window === "undefined") return null;

  const tryDecode = (val: string | null): string | null => {
    if (!val) return null;
    try {
      const decoded = decodeURIComponent(val);
      return decoded.includes("hash=") || decoded.includes("auth_date=") ? decoded : val;
    } catch (_e) {
      return val;
    }
  };

  // 1. hash fragment
  const rawHash = window.location.hash.startsWith("#")
    ? window.location.hash.slice(1)
    : window.location.hash;
  if (rawHash) {
    const hashParams = new URLSearchParams(rawHash);
    const fromHash =
      hashParams.get("tgWebAppData") ??
      hashParams.get("initData") ??
      hashParams.get("max_init_data");
    const decoded = tryDecode(fromHash);
    if (decoded && decoded.includes("hash=")) return decoded;

    // Fallback: hash itself looks like initData
    if (rawHash.includes("auth_date=") && rawHash.includes("hash=")) {
      return rawHash;
    }
  }

  // 2. search params
  const search = new URLSearchParams(window.location.search);
  const fromSearch =
    search.get("initData") ??
    search.get("tgWebAppData") ??
    search.get("max_init_data");
  const decodedSearch = tryDecode(fromSearch);
  if (decodedSearch && decodedSearch.includes("hash=")) return decodedSearch;

  return null;
}

function clearInitDataFromUrl() {
  if (typeof window === "undefined") return;
  const url = window.location.pathname;
  window.history.replaceState({}, "", url);
}

export function useMAXAuth(): MAXAuthResult {
  const [status, setStatus] = useState<MAXAuthStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const attemptedRef = useRef(false);
  const navigate = useNavigate();

  const verify = useCallback(async () => {
    const initData = extractInitData();
    if (!initData) {
      setStatus("idle");
      return;
    }

    setStatus("verifying");
    setError(null);

    try {
      const { data, error: fnErr } = await supabase.functions.invoke("verify-max-auth", {
        body: { initData },
      });

      if (fnErr) {
        const message =
          (fnErr as any)?.context?.error ||
          fnErr.message ||
          "Не удалось проверить авторизацию MAX";
        setError(message);
        setStatus("error");
        return;
      }

      const session = (data as any)?.session;
      if (!session?.access_token || !session?.refresh_token) {
        setError("Сервер не вернул сессию");
        setStatus("error");
        return;
      }

      const { error: setErr } = await supabase.auth.setSession({
        access_token: session.access_token,
        refresh_token: session.refresh_token,
      });
      if (setErr) {
        setError("Не удалось установить сессию: " + setErr.message);
        setStatus("error");
        return;
      }

      clearInitDataFromUrl();
      setStatus("success");

      // Redirect to dashboard root only if user landed on a public auth/landing page
      if (typeof window !== "undefined") {
        const path = window.location.pathname;
        if (path === "/auth" || path === "/landing") {
          navigate("/", { replace: true });
        }
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Неизвестная ошибка";
      setError(msg);
      setStatus("error");
    }
  }, []);

  useEffect(() => {
    if (attemptedRef.current) return;
    attemptedRef.current = true;
    void verify();
  }, [verify]);

  const retry = useCallback(() => {
    attemptedRef.current = false;
    void verify();
  }, [verify]);

  return { status, error, retry };
}
